import React, { Dispatch, useCallback, useMemo } from 'react';
import { Button, IconButton, Stack, SxProps } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import store, { IAppReduxStore } from '../../../redux/store';
import { labelFontSemiBold, vars } from '../../../Theme';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiViewColumnOutline, mdiBookmarkOutline } from '@mdi/js';
import { setShowColumnsMenu } from '../../../redux/user/sliceUserSettings';
import ViewControls from '../ViewControls';
import ViewControlsButton from '../ViewControlsButton';
import ProjectAreaIndicator from './ProjectAreaIndicator';
import { fetchDocuments, fetchViewData } from '../../../util/ProjectUtils';
import RefreshButton from '../../General/RefreshButton/RefreshButton';
import ProjectCreationActionsMenu from './ProjectCreationActionsMenu';
import ProjectColumnsViewsMenu from './ProjectColumnsViewsMenu';
import { saveCustomColumnsView } from '../../../redux/project/sliceProjectViewData';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../General/Toast/Toast';
import ScrollableTabs from '../../General/ScrollableTabs/ScrollableTabs';
import ProjectAdvancedSearch from './ProjectAdvancedSearch';
import { toNumberOrZero } from '../../../util/StringUtils';
import DataGridFilterButton from '../../General/DataGridFilterButton/DataGridFilterButton';

const iconButtonSx: SxProps = {
    width: '34px',
    height: '34px',
    borderRadius: '5px',
    minWidth: '34px',
    padding: '0',
    border: `1px solid ${vars.lightGray}`,
    '&:hover': {
        backgroundColor: vars.fillGray,
    },
};

/**
 * Component that allows the user to change how their projects are displayed.
 */
export default () => {
    /** Configure component translation. */
    const { i18n, t } = useTranslation('translations', { keyPrefix: 'docTable' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    /** Allow component to change the URL. */
    const navigate = useNavigate();

    const params = useParams();
    const { replicaId, areaName, viewName } = params;

    /** Get relevant values from Redux store. */
    const areas = useSelector((store: IAppReduxStore) => store.projectAreas.data);
    const views = useSelector((store: IAppReduxStore) => store.projectViews.data);
    const { showColumnsMenu } = useSelector((store: IAppReduxStore) => store.userSettings);
    const documentsLoading = useSelector(
        (store: IAppReduxStore) => store.projectDocuments.runPagination,
    );
    const fieldsCustom = useSelector(
        (store: IAppReduxStore) => store.projectViewData.fieldsCustom.data,
    );
    const schemaCustom = useSelector(
        (store: IAppReduxStore) => store.projectViewData.schemaCustom.data,
    );
    const { dataToSave, loadingSaveCustomColumnsView } = useSelector(
        (store: IAppReduxStore) => store.projectViewData,
    );

    /**
     * Toggles the visibility of the data grid's columns menu.
     */
    const toggleShowColumnsMenu = useCallback(() => {
        dispatch(setShowColumnsMenu(!showColumnsMenu));
    }, [showColumnsMenu]);

    const handleSaveCustomColumnsView = async () => {
        await store.dispatch(
            saveCustomColumnsView({
                replicaId,
                viewName: `${areaName}.${viewName}`,
            }),
        );

        fetchViewData(params);
        enqueueSnackbar(t('toastCustomColumnsViewSaveSuccess'), {
            severity: ToastSeverity.Success,
        });
    };

    const viewButtons = useMemo(() => {
        const viewsPerArea = Object.keys(views ?? {}).filter(x => views[x].areaId === areaName);
        const viewsPerAreaStandard = viewsPerArea
            .filter(x => !views?.[x].configurable)
            .sort((a, b) => toNumberOrZero(views?.[a].order) - toNumberOrZero(views?.[b].order));
        const viewsPerAreaConfigurable = viewsPerArea
            .filter(x => views?.[x].configurable)
            .sort((a, b) => toNumberOrZero(views?.[a].order) - toNumberOrZero(views?.[b].order));

        return [...viewsPerAreaStandard, ...viewsPerAreaConfigurable].map(x => (
            <ViewControlsButton
                key={x}
                label={views?.[x].translations[i18n.language] ?? x}
                active={x.split('.').pop() === viewName}
                onClick={() => navigate(`/project/${replicaId}/${x.split('.').join('/')}`)}
            />
        ));
    }, [views, viewName, i18n.language]);

    if (!areas) {
        return <></>;
    }

    return (
        <ViewControls
            header={<ProjectAreaIndicator />}
            row1={
                <Stack direction='row' sx={{ gap: '16px' }}>
                    <ProjectAdvancedSearch />
                    <ProjectCreationActionsMenu />
                </Stack>
            }
            row2={
                <Stack
                    direction='row'
                    sx={{
                        flexGrow: 4,
                        alignItems: 'center',
                        p: 1,
                    }}
                >
                    <ScrollableTabs>{viewButtons}</ScrollableTabs>
                    <Stack
                        direction='row'
                        spacing={1}
                        sx={{
                            flexGrow: 0,
                        }}
                    >
                        {dataToSave && (
                            <Button
                                sx={{
                                    flexDirection: 'row',
                                    height: '34px',
                                    borderRadius: '5px',
                                    minWidth: '34px',
                                    px: 1,
                                    fontSize: '14px',
                                    color: vars.font,
                                    textTransform: 'none',
                                    borderColor: vars.lightGray,
                                    ...labelFontSemiBold,
                                }}
                                variant='outlined'
                                onClick={handleSaveCustomColumnsView}
                                loading={loadingSaveCustomColumnsView}
                                startIcon={
                                    <Icon path={mdiBookmarkOutline} color={vars.font} size={1} />
                                }
                            >
                                {t('saveView')}
                            </Button>
                        )}
                        {fieldsCustom.length !== 0 && schemaCustom.length !== 0 && (
                            <ProjectColumnsViewsMenu />
                        )}
                        <IconButton sx={iconButtonSx} onClick={toggleShowColumnsMenu}>
                            <Icon path={mdiViewColumnOutline} size={1} color={vars.font} />
                        </IconButton>

                        <RefreshButton
                            handleOnClick={() => {
                                fetchDocuments(params);
                            }}
                            isLoading={documentsLoading}
                        />

                        <DataGridFilterButton />
                    </Stack>
                </Stack>
            }
        />
    );
};
